import type { QueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/shared/constants';

const SESSION_QUERY_KEY = [QUERY_KEYS.AUTH_SESSION] as const;
const PERMISSIONS_QUERY_KEY = [QUERY_KEYS.PERMISSIONS] as const;

let queryClientRef: QueryClient | null = null;

/**
 * Registers the app `QueryClient` so HTTP layers can align the auth session
 * cache when the BFF returns 401 (session cleared server-side).
 *
 * Call from `AuthProvider` (inside `QueryClientProvider`).
 */
export function bindUnauthorizedSessionSync(client: QueryClient): void {
  queryClientRef = client;
}

export function unbindUnauthorizedSessionSync(): void {
  queryClientRef = null;
}

/** Call from HTTP layers when a BFF response indicates the session is no longer valid. */
export function syncAuthSessionOnUnauthorizedResponse(status: number): void {
  if (status !== 401 || !queryClientRef) return;
  queryClientRef.setQueryData(SESSION_QUERY_KEY, { authenticated: false });
  queryClientRef.removeQueries({ queryKey: PERMISSIONS_QUERY_KEY });
}

/**
 * After a `fetch` to an authenticated BFF route: if 401, sync session cache and throw
 * so React Query does not keep stale `authenticated: true`.
 */
export function assertAuthorizedOrClearSession(res: Response): void {
  if (res.status !== 401) return;
  syncAuthSessionOnUnauthorizedResponse(401);
  throw new Error('Session expired');
}
